import {
  Component,
  computed,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import { EventStatusCode, STATUS_DEF_MAP } from '../../api/eventStatus.token';
import { CommonModule, CurrencyPipe } from '@angular/common';
import { RouterLink } from '@angular/router';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { AgendaService } from '../../../services/business';
import { PaymentsService } from '../../../services/payment.service';
import { ProductsService } from '../../../services/products.service';
import { ReceiptFormat, SalesService } from '../../../services/sales.service';
import {
  AgendaEvent,
  UpdateAgendaEventDto,
  ServicePayment,
  ClientPaymentHistory,
  Product,
  ProductSaleItem,
} from '../../interfaces';
import { ToastService } from '../../services';
import {
  formatRangeEs,
  parseDbUtc,
} from '../../common/utils';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs';

type ViewMode = 'event' | 'payment';

interface PaymentMethodOption {
  value: string;
  label: string;
  icon: string;
}

@Component({
  selector: 'app-event-view',
  imports: [CommonModule, ReactiveFormsModule, CurrencyPipe, RouterLink],
  templateUrl: './event-view.html',
  styleUrl: './event-view.css',
  // While charging, the view fills the modal body: only the bill lines scroll and the charge button stays visible
  host: { '[class.ev-fill]': "viewMode() === 'payment'" },
})
export class EventView implements OnInit, OnDestroy {
  @Input() eventId?: string | null;
  @Output() saved = new EventEmitter<void>();
  @Output() openPayment = new EventEmitter<string>();
  @Output() sizeChanged = new EventEmitter<string>();
  @Output() paymentCompleted = new EventEmitter<void>();

  private statusDefs = inject(STATUS_DEF_MAP);
  private agendaApi = inject(AgendaService);
  private paymentsService = inject(PaymentsService);
  private productsService = inject(ProductsService);
  private salesService = inject(SalesService);
  private toastService = inject(ToastService);
  private fb = inject(FormBuilder);

  protected readonly Math = Math;

  // View state
  viewMode = signal<ViewMode>('event');

  // Event data
  statusCode = signal<EventStatusCode>('scheduled');
  dateLine = signal<string | null>(null);
  agendaEvent = signal<AgendaEvent>({} as AgendaEvent);
  isSavingStatus = signal<boolean>(false);
  isLoadingData = signal<boolean>(false);

  statusOptions = computed(() =>
    Object.entries(this.statusDefs).map(([code, def]) => ({
      code: code as EventStatusCode,
      label: this.statusLabel(def.name),
      color: def.color ?? '#6B7280',
      id: def.eventStatusId,
    })),
  );

  currentStatus = computed(
    () => this.statusOptions().find((s) => s.code === this.statusCode()) ?? null,
  );

  // Header facts for the appointment
  start = computed(() => {
    const d = this.agendaEvent().startDate;
    return d ? parseDbUtc(d) : null;
  });
  end = computed(() => {
    const d = this.agendaEvent().endDate;
    return d ? parseDbUtc(d) : null;
  });
  durationMinutes = computed(() => {
    const s = this.start();
    const e = this.end();
    return s && e ? Math.round((+e - +s) / 60000) : 0;
  });
  clientInitials = computed(() =>
    (this.agendaEvent().clientName ?? '')
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]!.toUpperCase())
      .join(''),
  );
  professionalColor = computed(
    () => this.agendaEvent().professionalColor || this.agendaEvent().color || '#465FFF',
  );
  phoneHref = computed(() => {
    const phone = this.agendaEvent().clientPhone?.replace(/[^\d+]/g, '');
    return phone ? `tel:${phone}` : null;
  });

  // Pricing computed
  hasPromotion = computed(() => {
    const event = this.agendaEvent();
    return !!(event.promotionId && event.promotionAppliedAmount && event.promotionAppliedAmount > 0);
  });

  servicePrice = computed(() => {
    const event = this.agendaEvent();
    return event.originalServicePrice ?? event.servicePrice ?? 0;
  });

  discountAmount = computed(() => {
    return this.agendaEvent().promotionAppliedAmount ?? 0;
  });

  finalPrice = computed(() => {
    const event = this.agendaEvent();
    if (event.finalPrice != null) return event.finalPrice;
    return Math.max(0, this.servicePrice() - this.discountAmount());
  });

  promotionDisplay = computed(() => {
    const event = this.agendaEvent();
    if (!this.hasPromotion()) return null;
    const discountType = event.promotionDiscountType?.toLowerCase();
    const value = event.promotionValue ?? 0;
    if (discountType === 'percentage') return `${value}%`;
    if (discountType === 'fixed') {
      return value.toLocaleString('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 });
    }
    return null;
  });

  // Payment data
  payment = signal<ServicePayment | null>(null);
  history = signal<ClientPaymentHistory | null>(null);
  isPaymentLoading = signal(false);
  isDownloadingReceipt = signal(false);
  downloadingFormat = signal<ReceiptFormat | null>(null);
  lastPaymentId = signal<string | null>(null);

  // Products
  products = signal<Product[]>([]);
  selectedProducts = signal<ProductSaleItem[]>([]);
  isSearchingProducts = signal(false);
  showProductSearch = signal(false);
  // The note field stays collapsed so the charge button fits without scrolling
  showNote = signal(false);

  // Payment methods
  readonly paymentMethods: PaymentMethodOption[] = [
    { value: 'cash', label: 'Efectivo', icon: 'M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z' },
    { value: 'transfer', label: 'Transferencia', icon: 'M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4' },
    { value: 'card', label: 'Tarjeta', icon: 'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z' },
    { value: 'other', label: 'Otro', icon: 'M8 12h.01M12 12h.01M16 12h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
  ];
  private readonly creditMethod: PaymentMethodOption = {
    value: 'credit',
    label: 'Saldo a favor',
    icon: 'M21 12a2.25 2.25 0 0 0-2.25-2.25H15a3 3 0 1 1-6 0H5.25A2.25 2.25 0 0 0 3 12m18 0v6a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 18v-6m18 0V9M3 12V9m18 0a2.25 2.25 0 0 0-2.25-2.25H5.25A2.25 2.25 0 0 0 3 9m18 0V6a2.25 2.25 0 0 0-2.25-2.25H5.25A2.25 2.25 0 0 0 3 6v3',
  };
  private readonly methodLabels: Record<string, string> = {
    cash: 'Efectivo',
    transfer: 'Transferencia',
    card: 'Tarjeta',
    other: 'Otro',
    credit: 'Saldo a favor',
    epayco_link: 'Pago en línea',
  };

  // Forms
  manualForm = this.fb.group({
    amount: [0, [Validators.required, Validators.min(1)]],
    method: ['cash', Validators.required],
    notes: [''],
  });

  productSearchForm = this.fb.group({
    searchTerm: [''],
  });

  manualAmountDisplay = signal('');

  // Payment computed
  serverPendingAmount = computed(() => this.payment()?.pendingAmount ?? 0);
  pendingAmount = computed(() => this.serverPendingAmount() + this.pendingProductsTotal());
  hasPendingAmount = computed(() => this.pendingAmount() > 0);
  isPaid = computed(() => this.payment()?.status === 'paid' && this.selectedProducts().length === 0);

  availableCredit = computed(() => {
    const balance = this.history()?.walletBalance ?? 0;
    return balance > 0 ? balance : 0;
  });

  canUseCredit = computed(
    () => this.availableCredit() > 0 && this.pendingAmount() > 0,
  );

  /** Saldo a favor only shows up as a method when the client has some */
  methodOptions = computed(() =>
    this.canUseCredit() ? [...this.paymentMethods, this.creditMethod] : this.paymentMethods,
  );

  selectedMethod = signal('cash');
  enteredAmount = signal(0);

  installments = computed(() => this.payment()?.installments ?? []);

  /** A completed (or refunded) payment is closed: no more products can be added to it */
  canAddProducts = computed(() => {
    const status = this.payment()?.status;
    return !!status && status !== 'paid' && status !== 'refunded';
  });
  paidAmount = computed(() => this.payment()?.paidAmount ?? 0);
  paidPercent = computed(() => {
    const total = this.paidAmount() + this.pendingAmount();
    return total > 0 ? Math.min(100, Math.round((this.paidAmount() / total) * 100)) : 0;
  });
  payState = computed<'paid' | 'partial' | 'pending'>(() => {
    if (this.isPaid()) return 'paid';
    return this.paidAmount() > 0 ? 'partial' : 'pending';
  });

  /** Why the entered amount can't be charged, or null when it can */
  amountError = computed(() => {
    const amount = this.enteredAmount();
    if (amount <= 0) return 'Escribe el monto que vas a cobrar.';
    if (amount > this.pendingAmount()) return 'El monto es mayor que lo que falta por pagar.';
    if (this.selectedMethod() === 'credit' && amount > this.availableCredit()) {
      return 'El monto es mayor que el saldo a favor disponible.';
    }
    return null;
  });

  // Products from payment items (already confirmed on server)
  confirmedProducts = computed(() =>
    (this.payment()?.items ?? []).filter(i => i.itemType === 'Product'),
  );

  confirmedProductsTotal = computed(() =>
    this.confirmedProducts().reduce((sum, i) => sum + i.total, 0),
  );

  // Products selected by user (not yet confirmed)
  pendingProductsTotal = computed(() =>
    this.selectedProducts().reduce(
      (sum, item) => sum + item.quantity * item.unitPrice,
      0,
    ),
  );

  productsTotal = computed(() => this.confirmedProductsTotal() + this.pendingProductsTotal());

  grandTotal = computed(() => this.finalPrice() + this.productsTotal());

  constructor() {
    this.syncManualAmountDisplay();

    this.productSearchForm
      .get('searchTerm')
      ?.valueChanges.pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((term) => {
          this.isSearchingProducts.set(true);
          return this.productsService.getProducts({
            searchTerm: term || undefined,
            isActive: true,
            pageSize: 20,
          });
        }),
      )
      .subscribe({
        next: (response) => {
          this.products.set(response.products);
          this.isSearchingProducts.set(false);
        },
        error: () => {
          this.isSearchingProducts.set(false);
        },
      });
  }

  ngOnInit(): void {
    if (this.eventId) this.loadEvent(this.eventId!);
  }

  ngOnDestroy(): void {}

  // --- Event Loading ---

  loadEvent(eventId: string) {
    this.isLoadingData.set(true);
    this.agendaApi.getEventById(eventId).subscribe({
      next: (res) => {
        this.agendaEvent.set(res);
        this.statusCode.set(res.statusCode as EventStatusCode);
        this.dateLine.set(
          formatRangeEs(parseDbUtc(res.startDate), parseDbUtc(res.endDate)),
        );
        this.isLoadingData.set(false);
      },
      error: (err) => {
        console.error(err);
        this.isLoadingData.set(false);
      },
    });
  }

  /** "Agendado" → "Agendada": the modal talks about a cita */
  statusLabel(name?: string | null): string {
    if (!name) return '';
    return name.endsWith('do') ? name.slice(0, -1) + 'a' : name;
  }

  formatDuration(minutes: number): string {
    if (minutes < 60) return `${minutes} min`;
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return m ? `${h} h ${m} min` : `${h} h`;
  }

  setStatus(code: EventStatusCode) {
    const evt: AgendaEvent | null = this.agendaEvent();
    if (!evt || !this.eventId) return;

    const def = this.statusDefs[code];
    const statusId = def?.eventStatusId ?? (def as any)?.eventStatusId;
    if (!statusId) {
      console.error('No se encontro statusId para el codigo', code);
      return;
    }

    const prevCode = this.statusCode();
    this.statusCode.set(code);
    this.isSavingStatus.set(true);
    const body: UpdateAgendaEventDto = {
      clientId: evt.clientId,
      serviceId: evt.serviceId,
      statusId,
      startDate: parseDbUtc(evt.startDate),
      endDate: parseDbUtc(evt.endDate),
      notes: evt.notes ?? '',
      paymentCompleted: !!evt.paymentCompleted,
    };

    this.agendaApi.updateAgendaEvent(this.eventId!, body).subscribe({
      next: () => {
        this.isSavingStatus.set(false);
        this.saved.emit();
      },
      error: () => {
        this.statusCode.set(prevCode);
        this.isSavingStatus.set(false);
      },
    });
  }

  // --- Payment Mode ---

  enterPaymentMode(): void {
    this.viewMode.set('payment');
    this.sizeChanged.emit('md-1');
    this.loadPaymentData();
  }

  backToEventView(): void {
    this.viewMode.set('event');
    this.sizeChanged.emit('sm');
  }

  triggerPaymentDrawer() {
    // Now handled inline
    this.enterPaymentMode();
  }

  private loadPaymentData(): void {
    const evt = this.agendaEvent();
    if (!evt?.eventId) return;
    this.isPaymentLoading.set(true);

    const totalAmount = this.getServiceTotal(evt);
    this.paymentsService
      .createServicePayment({
        eventId: evt.eventId,
        clientId: evt.clientId,
        serviceId: evt.serviceId,
        amount: totalAmount,
        currency: 'COP',
        sessionPackageId: evt.sessionPackageId ?? undefined,
      })
      .subscribe({
        next: (payment) => {
          this.payment.set(payment);
          this.lastPaymentId.set(payment.paymentId);
          const amountToShow = payment.pendingAmount > 0 ? payment.pendingAmount : 0;
          this.manualForm.patchValue({ amount: amountToShow }, { emitEvent: false });
          this.syncManualAmountDisplay();
          this.isPaymentLoading.set(false);
          this.loadHistory();
        },
        error: () => {
          this.isPaymentLoading.set(false);
          this.toastService.show('No pudimos cargar la información de pago.', 'warning');
        },
      });
  }

  private reloadPayment(onDone?: () => void): void {
    const evt = this.agendaEvent();
    if (!evt?.eventId) {
      onDone?.();
      return;
    }
    const totalAmount = this.getServiceTotal(evt);
    this.paymentsService
      .createServicePayment({
        eventId: evt.eventId,
        clientId: evt.clientId,
        serviceId: evt.serviceId,
        amount: totalAmount,
        currency: 'COP',
        sessionPackageId: evt.sessionPackageId ?? undefined,
      })
      .subscribe({
        next: (payment) => {
          this.payment.set(payment);
          this.lastPaymentId.set(payment.paymentId);
          const amountToShow = payment.pendingAmount > 0 ? payment.pendingAmount : 0;
          this.manualForm.patchValue({ amount: amountToShow }, { emitEvent: false });
          this.syncManualAmountDisplay();
          onDone?.();
        },
        error: () => onDone?.(),
      });
  }

  private loadHistory(): void {
    const evt = this.agendaEvent();
    if (!evt?.clientId) return;
    this.paymentsService.getClientPaymentHistory(evt.clientId).subscribe({
      next: (history) => this.history.set(history),
      error: () => {},
    });
  }

  private getServiceTotal(evt: AgendaEvent): number {
    if (evt.finalPrice != null) return evt.finalPrice;
    const price = evt.originalServicePrice ?? evt.servicePrice ?? 0;
    const discount = evt.promotionAppliedAmount ?? 0;
    return Math.max(0, price - discount);
  }

  // --- Products ---

  toggleProductSearch(): void {
    if (!this.canAddProducts()) return;
    this.showProductSearch.set(!this.showProductSearch());
    if (this.showProductSearch() && this.products().length === 0) {
      this.loadProducts();
    }
  }

  loadProducts(): void {
    this.productsService
      .getProducts({ isActive: true, pageSize: 50 })
      .subscribe({
        next: (response) => this.products.set(response.products),
        error: () => this.toastService.show('Error al cargar productos', 'warning'),
      });
  }

  addProduct(product: Product): void {
    if (product.stock === 0 || !this.canAddProducts()) return;

    const existing = this.selectedProducts().find(
      (item) => item.productId === product.productId,
    );

    if (existing) {
      if (existing.quantity >= product.stock) {
        this.toastService.show(`Stock disponible: ${product.stock}`, 'warning');
        return;
      }
      this.selectedProducts.set(
        this.selectedProducts().map((item) =>
          item.productId === product.productId
            ? { ...item, quantity: item.quantity + 1 }
            : item,
        ),
      );
    } else {
      this.selectedProducts.set([
        ...this.selectedProducts(),
        { productId: product.productId, quantity: 1, unitPrice: product.price },
      ]);
    }
    this.syncAmountWithProducts();
  }

  removeProduct(productId: string): void {
    this.selectedProducts.set(
      this.selectedProducts().filter((item) => item.productId !== productId),
    );
    this.syncAmountWithProducts();
  }

  updateProductQuantity(productId: string, quantity: number): void {
    if (quantity <= 0) {
      this.removeProduct(productId);
      return;
    }
    const product = this.products().find((p) => p.productId === productId);
    if (!product) return;
    if (quantity > product.stock) {
      this.toastService.show(`Stock disponible: ${product.stock}`, 'warning');
      return;
    }
    this.selectedProducts.set(
      this.selectedProducts().map((item) =>
        item.productId === productId ? { ...item, quantity } : item,
      ),
    );
    this.syncAmountWithProducts();
  }

  private syncAmountWithProducts(): void {
    const amount = this.pendingAmount();
    this.manualForm.patchValue({ amount }, { emitEvent: false });
    this.syncManualAmountDisplay();
  }

  getProductName(productId: string): string {
    return this.products().find((p) => p.productId === productId)?.name || 'Producto';
  }

  getProductStock(productId: string): number {
    return this.products().find((p) => p.productId === productId)?.stock || 0;
  }

  getProductItemTotal(item: ProductSaleItem): number {
    return item.quantity * item.unitPrice;
  }

  private sendProductsToPayment(): Promise<void> {
    if (!this.payment() || this.selectedProducts().length === 0) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      this.paymentsService
        .addProductsToPayment(this.payment()!.paymentId, {
          products: this.selectedProducts(),
        })
        .subscribe({
          next: (res) => {
            const current = this.payment();
            if (current) {
              this.payment.set({
                ...current,
                amount: res.newAmount,
                pendingAmount: res.newPendingAmount,
              });
            }
            this.selectedProducts.set([]);
            this.showProductSearch.set(false);
            resolve();
          },
          error: (err) => {
            this.toastService.show(
              err.error?.message || 'Error al agregar productos al pago',
              'warning',
            );
            reject(err);
          },
        });
    });
  }

  // --- Payment ---

  selectPaymentMethod(method: string): void {
    const wasCredit = this.manualForm.value.method === 'credit';
    this.manualForm.patchValue({ method }, { emitEvent: false });
    if (method === 'credit') {
      // Saldo a favor covers as much of the debt as it can
      this.manualForm.patchValue(
        { amount: Math.min(this.availableCredit(), this.pendingAmount()) },
        { emitEvent: false },
      );
    } else if (wasCredit) {
      this.manualForm.patchValue({ amount: this.pendingAmount() }, { emitEvent: false });
    }
    this.syncManualAmountDisplay();
  }

  /** Quick amount chips: everything pending, or half of it */
  setAmount(amount: number): void {
    this.manualForm.patchValue({ amount: Math.round(amount) }, { emitEvent: false });
    this.syncManualAmountDisplay();
  }

  registerPayment(): void {
    if (!this.payment()) return;
    const { amount, method, notes } = this.manualForm.getRawValue();
    const useClientCredit = method === 'credit';
    let numericAmount = Number(amount);
    if (Number.isNaN(numericAmount)) numericAmount = 0;

    if (this.amountError()) return;
    if (!useClientCredit && this.manualForm.invalid) return;

    if (useClientCredit) {
      if (!this.canUseCredit()) {
        this.toastService.show('El cliente no tiene saldo a favor disponible.', 'warning');
        return;
      }
      if (numericAmount <= 0 || numericAmount > this.availableCredit()) {
        numericAmount = Math.min(this.availableCredit(), this.pendingAmount());
      }
    }

    if (!numericAmount || numericAmount <= 0 || !method) return;

    this.isPaymentLoading.set(true);

    // Send pending products first (if any), then register payment
    if (this.selectedProducts().length > 0) {
      this.sendProductsToPayment().then(() => {
        const updatedAmount = Math.min(numericAmount, this.payment()!.pendingAmount);
        if (updatedAmount <= 0) {
          this.isPaymentLoading.set(false);
          return;
        }
        this.doRegisterPayment(updatedAmount, method, notes?.trim() || undefined, useClientCredit);
      }).catch(() => {
        this.isPaymentLoading.set(false);
      });
    } else {
      this.doRegisterPayment(numericAmount, method, notes?.trim() || undefined, useClientCredit);
    }
  }

  private doRegisterPayment(amount: number, method: string, notes: string | undefined, useClientCredit: boolean): void {
    const payload = {
      amount,
      method,
      notes,
      ...(useClientCredit ? { useClientCredit: true } : {}),
    };

    this.paymentsService
      .registerManualPayment(this.payment()!.paymentId, payload)
      .subscribe({
        next: (res: any) => {
          this.toastService.show('Pago registrado correctamente.', 'success');
          // Stay on this view, as with "Ver pago": the refreshed payment shows the detail and the receipts.
          // The button keeps its loading state until then, so the old pending amount never flashes.
          this.reloadPayment(() => this.isPaymentLoading.set(false));
          this.loadHistory();
          this.manualForm.patchValue(
            { amount: res.pending ?? 0, notes: '', method: 'cash' },
            { emitEvent: false },
          );
          this.showNote.set(false);
          this.syncManualAmountDisplay();
          // Refresh the calendar without closing the modal (saved closes it), and the event so it reads as paid
          this.paymentCompleted.emit();
          if (this.agendaEvent().eventId) this.loadEvent(this.agendaEvent().eventId!);
        },
        error: () => {
          this.isPaymentLoading.set(false);
          this.toastService.show('No pudimos registrar el pago.', 'warning');
        },
      });
  }

  // --- Receipt ---

  downloadReceipt(format: ReceiptFormat = 'half-letter'): void {
    const paymentId = this.lastPaymentId();
    if (!paymentId) {
      this.toastService.show('No hay comprobante disponible', 'warning');
      return;
    }
    if (this.isDownloadingReceipt()) return;

    this.isDownloadingReceipt.set(true);
    this.downloadingFormat.set(format);
    this.salesService.downloadReceipt(paymentId, format).subscribe({
      next: (blob) => {
        if (blob.size === 0) {
          this.toastService.show('Error al generar el comprobante PDF', 'error');
          this.isDownloadingReceipt.set(false);
          this.downloadingFormat.set(null);
          return;
        }

        const pdfBlob = blob.type === 'application/pdf'
          ? blob
          : new Blob([blob], { type: 'application/pdf' });

        const url = window.URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `comprobante-${paymentId.slice(0, 8)}${format === 'ticket' ? '-tirilla' : ''}.pdf`;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();

        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }, 100);

        this.isDownloadingReceipt.set(false);
        this.downloadingFormat.set(null);
        this.toastService.show(
          format === 'ticket' ? 'Tirilla descargada' : 'Comprobante descargado',
          'success',
        );
      },
      error: (err) => {
        this.isDownloadingReceipt.set(false);
        this.downloadingFormat.set(null);
        if (err.error instanceof Blob) {
          err.error.text().then((text: string) => {
            try {
              const error = JSON.parse(text);
              this.toastService.show(error.message || 'Error al descargar el comprobante', 'error');
            } catch {
              this.toastService.show('Error al descargar el comprobante', 'error');
            }
          }).catch(() => {
            this.toastService.show('Error al descargar el comprobante', 'error');
          });
        } else {
          this.toastService.show(err.error?.message || 'Error al descargar el comprobante', 'error');
        }
      },
    });
  }

  // --- Helpers ---

  getUnitPricePerSession(): number {
    const evt = this.agendaEvent();
    if (!evt) return 0;
    const totalSessions = evt.totalSessions ?? 1;
    if (totalSessions <= 1) return evt.originalServicePrice ?? evt.servicePrice ?? 0;
    const totalPrice = evt.finalPrice ?? evt.originalServicePrice ?? evt.servicePrice ?? 0;
    return totalPrice / totalSessions;
  }

  onManualAmountInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const digits = input.value.replace(/[^\d]/g, '');
    const numeric = digits ? Number(digits) : 0;
    this.manualForm.patchValue({ amount: numeric }, { emitEvent: false });
    this.manualAmountDisplay.set(this.formatCurrency(numeric));
    this.enteredAmount.set(numeric);
  }

  onAmountFocus(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = this.manualForm.get('amount')?.value || 0;
    input.value = value.toString();
  }

  onAmountBlur(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = this.manualForm.get('amount')?.value || 0;
    input.value = this.formatCurrency(value);
  }

  methodLabel(method: string): string {
    return this.methodLabels[method] ?? method;
  }

  formatCurrency(value: number): string {
    if (!value) return '';
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  }

  formatDate(dateStr: string): string {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-CO', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });
  }

  formatTime(dateStr: string): string {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('es-CO', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  private syncManualAmountDisplay(): void {
    this.manualAmountDisplay.set(
      this.formatCurrency(this.manualForm.value.amount ?? 0),
    );
    this.enteredAmount.set(Number(this.manualForm.value.amount ?? 0));
    this.selectedMethod.set(this.manualForm.value.method ?? 'cash');
  }
}
